import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import styled from "styled-components";
import toast from "react-hot-toast";
import { RootState } from "../store";
import {
  loginStart,
  loginSuccess,
  loginFailure,
} from "../store/slices/authSlice";
import { useLoginMutation, useValidate2FAMutation } from "../store/trader-api";

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url("/background.svg") no-repeat center center;
  background-size: cover;
  background-attachment: fixed;
  padding: 1rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--accent-primary-light);
    z-index: 1;
  }
`;

const LoginCard = styled.div`
  background: var(--bg-glass);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  box-shadow: var(--shadow-lg);
  padding: 2.5rem;
  width: 100%;
  max-width: 400px;
  position: relative;
  z-index: 2;
  border: 1px solid var(--border-light);

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 0.75rem;
  }
`;

const LogoSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Logo = styled.div`
  width: 80px;
  height: 80px;
  background: var(--bg-card);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: var(--shadow-md);

  img {
    width: 60px;
    height: auto;
    max-height: 60px;
  }
`;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.5rem;

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 0.75rem 1rem;
  border: 1px solid ${(props) => (props.hasError ? "var(--accent-error)" : "var(--border-input)")};
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: all 0.2s;
  background-color: var(--bg-card);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ErrorMessage = styled.span`
  font-size: 0.75rem;
  color: var(--accent-error);
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const LoginButton = styled.button<{ isLoading?: boolean }>`
  background: var(--gradient-avatar);
  color: #ffffff;
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(props) => (props.isLoading ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.isLoading ? 0.8 : 1)};
  transition: opacity 0.2s, box-shadow 0.2s;
  position: relative;
  min-height: 3rem;

  &:hover:not(:disabled) {
    box-shadow: var(--shadow-avatar);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const LoadingSpinner = styled.div`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-light);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: translateY(-50%) rotate(360deg);
    }
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-divider);
  color: var(--text-muted);
  font-size: 0.75rem;
`;

const TwoFAIcon = styled.div`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const TwoFAInput = styled.input`
  padding: 1rem;
  border: 2px solid var(--border-input);
  border-radius: 0.75rem;
  font-size: 1.5rem;
  text-align: center;
  letter-spacing: 0.5rem;
  font-weight: 700;
  color: var(--text-primary);
  background: var(--bg-card);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    letter-spacing: 0.3rem;
    font-size: 1rem;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: center;
  margin-top: 0.5rem;

  &:hover {
    color: var(--text-primary);
  }
`;

interface LoginFormData {
  login: string;
  password: string;
}

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useSelector(
    (state: RootState) => state.auth
  );
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [validate2FA, { isLoading: is2FALoading }] = useValidate2FAMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  // 2FA state
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    try {
      dispatch(loginStart());

      const result = await login(data).unwrap();

      // Check if 2FA is required
      if (result.requiresTwoFactor && result.tempToken) {
        setTempToken(result.tempToken);
        setShowTwoFactor(true);
        dispatch(loginFailure(""));
        return;
      }

      dispatch(
        loginSuccess({
          user: result.user,
          token: result.token,
          refreshToken: result.refreshToken,
        })
      );

      toast.success("Добро пожаловать в панель трейдера!");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (err: any) {
      console.error("Login error:", err);
      const errorMessage = err?.data?.message || "Ошибка входа";
      dispatch(loginFailure(errorMessage));
      toast.error(errorMessage);
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFactorCode || twoFactorCode.length < 6) {
      toast.error("Введите 6-значный код");
      return;
    }

    try {
      dispatch(loginStart());
      const result = await validate2FA({
        tempToken,
        code: twoFactorCode,
      }).unwrap();

      dispatch(
        loginSuccess({
          user: result.user,
          token: result.token,
          refreshToken: result.refreshToken,
        })
      );

      toast.success("Добро пожаловать в панель трейдера!");
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (err: any) {
      console.error("2FA error:", err);
      const errorMessage = err?.data?.message || "Неверный код 2FA";
      dispatch(loginFailure(errorMessage));
      toast.error(errorMessage);
      setTwoFactorCode("");
    }
  };

  const handleBackToLogin = () => {
    setShowTwoFactor(false);
    setTempToken("");
    setTwoFactorCode("");
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoSection>
          <Logo>
            <img src="/logo.svg" alt="Vangard Pay" />
          </Logo>
          <Title>Vangard Pay</Title>
          <Subtitle>
            {showTwoFactor
              ? "Введите код из приложения-аутентификатора"
              : "Войдите в панель управления трейдера"}
          </Subtitle>
        </LogoSection>

        {showTwoFactor ? (
          <Form onSubmit={handleTwoFactorSubmit}>
            <TwoFAIcon></TwoFAIcon>
            <FormGroup>
              <Label>Код подтверждения</Label>
              <TwoFAInput
                type="text"
                inputMode="numeric"
                maxLength={8}
                placeholder="000000"
                value={twoFactorCode}
                onChange={(e) =>
                  setTwoFactorCode(e.target.value.replace(/[^0-9-]/g, ""))
                }
                autoFocus
              />
              <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", textAlign: "center" }}>
                Откройте Google Authenticator и введите 6-значный код
              </span>
            </FormGroup>

            <LoginButton
              type="submit"
              disabled={isLoading || is2FALoading}
              isLoading={isLoading || is2FALoading}
            >
              {(isLoading || is2FALoading) && <LoadingSpinner />}
              Подтвердить
            </LoginButton>

            <BackButton type="button" onClick={handleBackToLogin}>
              ← Вернуться к логину
            </BackButton>
          </Form>
        ) : (
          <Form onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
              <Label htmlFor="login">Логин</Label>
              <Input
                id="login"
                type="text"
                placeholder="Введите логин"
                hasError={!!errors.login}
                {...register("login", {
                  required: "Поле обязательно для заполнения",
                  minLength: {
                    value: 3,
                    message: "Минимум 3 символа",
                  },
                })}
              />
              {errors.login && (
                <ErrorMessage>⚠️ {errors.login.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="password">Пароль</Label>
              <Input
                id="password"
                type="password"
                placeholder="Введите пароль"
                hasError={!!errors.password}
                {...register("password", {
                  required: "Поле обязательно для заполнения",
                  minLength: {
                    value: 6,
                    message: "Минимум 6 символов",
                  },
                })}
              />
              {errors.password && (
                <ErrorMessage>⚠️ {errors.password.message}</ErrorMessage>
              )}
            </FormGroup>

            <LoginButton
              type="submit"
              disabled={isLoading || isLoginLoading}
              isLoading={isLoading || isLoginLoading}
            >
              {(isLoading || isLoginLoading) && <LoadingSpinner />}
              Войти
            </LoginButton>
          </Form>
        )}

        <Footer>© 2026 Vangard Pay. Trader Panel.</Footer>
      </LoginCard>
    </LoginContainer>
  );
}

export default Login;
